import { useState } from 'react';
import { getLegalTexts } from '@/lib/legal/legal-texts';
import { IconChevronDown, IconChevronUp, IconShieldCheck, IconFileText } from '@tabler/icons-react';

interface LegalDisclosureProps {
  lang?: string;
  className?: string;
}

/**
 * Componente que muestra el texto legal RGPD + Mossos
 * Colapsable para no ocupar demasiado espacio
 */
export function LegalDisclosure({ lang = 'ca', className = '' }: LegalDisclosureProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const texts = getLegalTexts(lang);

  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-xl overflow-hidden ${className}`}>
      {/* Header siempre visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <IconShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">
              {lang === 'ca' ? 'Informació Legal' : 
               lang === 'es' ? 'Información Legal' :
               lang === 'fr' ? 'Information Légale' : 'Legal Information'}
            </h3>
            <p className="text-sm text-gray-500">
              {lang === 'ca' ? 'RGPD i Registre de Viatgers' :
               lang === 'es' ? 'RGPD y Registro de Viajeros' :
               lang === 'fr' ? 'RGPD et Enregistrement des Voyageurs' : 'GDPR and Traveler Registration'}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <IconChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <IconChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Contenido expandible */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
          {/* RGPD */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <IconShieldCheck className="h-4 w-4 text-blue-600" />
              <h4 className="font-medium text-gray-900">{texts.rgpdTitle}</h4>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-line">{texts.rgpdText}</p>
          </div>

          {/* Mossos */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <IconFileText className="h-4 w-4 text-amber-600" />
              <h4 className="font-medium text-gray-900">{texts.mossosTitle}</h4>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-line">{texts.mossosText}</p>
          </div>

          {/* Retención y derechos */}
          <div className="bg-white p-3 rounded-lg border border-gray-100 space-y-2">
            <p className="text-xs text-gray-500">{texts.dataRetention}</p>
            <p className="text-xs text-gray-500">{texts.rights}</p>
            <p className="text-xs text-gray-400">{texts.contact}</p>
          </div>
        </div>
      )}

      {/* Resumen siempre visible */}
      {!isExpanded && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-500">
            {lang === 'ca' ? 'Les seves dades seran tractades segons el RGPD i comunicades a Mossos d\'Esquadra segons RD 933/2021.' :
             lang === 'es' ? 'Sus datos serán tratados según el RGPD y comunicados a Mossos d\'Esquadra según RD 933/2021.' :
             lang === 'fr' ? 'Vos données seront traitées conformément au RGPD et communiquées aux Mossos d\'Esquadra selon RD 933/2021.' :
             'Your data will be processed according to GDPR and reported to Mossos d\'Esquadra as per RD 933/2021.'}
          </p>
        </div>
      )}
    </div>
  );
}
